<script setup lang="ts">
import type { CaseDetail, ConversationDetail } from "@maimu/contracts";
import { ATTACHMENT_ACCEPT, CASE_STATUSES } from "@maimu/contracts";
import { onMounted, ref, watch } from "vue";

import { api } from "@/lib/api.js";
import { renderMarkdown } from "@/lib/markdown.js";
import { currentUser } from "@/lib/session.js";

/**
 * One toimik (D21): the summary everything starts from, the timeline, the threads
 * and the files.
 *
 * Colleagues' conversations are readable but not continuable. Two people typing into
 * one transcript reads as neither of them; the timeline is the shared surface, and
 * that is what mAImu writes to as well.
 */
const props = defineProps<{ caseId: string }>();
const emit = defineEmits<{ (event: "back"): void; (event: "open-chat", caseId: string): void }>();

const kase = ref<CaseDetail | null>(null);
const loading = ref(true);
const busy = ref(false);
const error = ref<string | null>(null);

const note = ref("");
const member = ref("");
const editingSummary = ref(false);
const summaryDraft = ref("");
const reading = ref<ConversationDetail | null>(null);

const STATUS = { open: "avatud", resolved: "lahendatud", archived: "arhiveeritud" } as const;

function fail(cause: unknown, fallback: string): void {
  error.value = cause instanceof Error ? cause.message : fallback;
}

async function load(): Promise<void> {
  loading.value = true;
  try {
    kase.value = await api.case(props.caseId);
    summaryDraft.value = kase.value.summary;
  } catch (cause) {
    fail(cause, "Toimikut ei õnnestunud avada.");
  } finally {
    loading.value = false;
  }
}

async function run(action: () => Promise<CaseDetail | void>): Promise<void> {
  busy.value = true;
  error.value = null;
  try {
    const next = await action();
    if (next) {
      kase.value = next;
      summaryDraft.value = next.summary;
    } else {
      emit("back");
    }
  } catch (cause) {
    fail(cause, "Toiming ebaõnnestus.");
  } finally {
    busy.value = false;
  }
}

async function openConversation(id: string): Promise<void> {
  error.value = null;
  try {
    reading.value = await api.caseConversation(props.caseId, id);
  } catch (cause) {
    fail(cause, "Vestlust ei õnnestunud avada.");
  }
}

const toDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Faili lugemine ebaõnnestus."));
    reader.readAsDataURL(file);
  });

async function onFiles(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []).slice(0, 5);
  input.value = "";
  if (!files.length) return;

  const payload = await Promise.all(files.map(async (file) => ({ name: file.name, dataUrl: await toDataUrl(file) })));
  await run(() => api.addCaseFiles(props.caseId, payload));
}

const when = (iso: string): string =>
  new Date(iso).toLocaleString("et-EE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const kb = (bytes: number): string => `${Math.max(1, Math.round(bytes / 1024))} kB`;

watch(() => props.caseId, load);
onMounted(load);
</script>

<template>
  <section class="toimik">
    <p v-if="loading" class="hint">Laen…</p>

    <template v-else-if="kase">
      <header class="head">
        <div>
          <button class="btn-ghost" type="button" @click="emit('back')">← Toimikud</button>
          <h2>
            {{ kase.title }}
            <code v-if="kase.code">{{ kase.code }}</code>
          </h2>
          <p class="meta">
            Liikmed: {{ kase.members.join(", ") }} · {{ kase.conversations.length }} vestlust ·
            {{ kase.notes.length }} märkust · muudetud {{ when(kase.updatedAt) }}
          </p>
        </div>
        <div class="actions">
          <select
            :value="kase.status"
            :disabled="busy"
            @change="
              run(() =>
                api.updateCase(kase!.id, {
                  status: ($event.target as HTMLSelectElement).value as CaseDetail['status'],
                }),
              )
            "
          >
            <option v-for="status in CASE_STATUSES" :key="status" :value="status">{{ STATUS[status] }}</option>
          </select>
          <button class="btn" type="button" @click="emit('open-chat', kase.id)">Uus vestlus siin</button>
        </div>
      </header>

      <p v-if="error" class="error">{{ error }}</p>

      <section class="block">
        <div class="block-head">
          <h3>Kokkuvõte</h3>
          <button class="btn-ghost" type="button" @click="editingSummary = !editingSummary">
            {{ editingSummary ? "Loobu" : "Muuda" }}
          </button>
        </div>
        <p class="hint">
          See tekst läheb iga selle toimiku vestluse algusesse. mAImu võib seda ise uuendada; vanad versioonid jäävad
          alles ({{ kase.revisions }} versiooni).
        </p>

        <template v-if="editingSummary">
          <textarea
            v-model="summaryDraft"
            rows="10"
            placeholder="Mis probleem, mis on teada, mis on välistatud, mis on järgmine samm."
          />
          <button
            class="btn"
            type="button"
            :disabled="busy"
            @click="run(() => api.updateCase(kase!.id, { summary: summaryDraft })).then(() => (editingSummary = false))"
          >
            Salvesta kokkuvõte
          </button>
        </template>
        <!-- eslint-disable-next-line vue/no-v-html -- markdown is escaped in renderMarkdown -->
        <div v-else-if="kase.summary" class="prose" v-html="renderMarkdown(kase.summary)" />
        <p v-else class="empty">Kokkuvõtet ei ole veel. Kirjuta see, või lase mAImul see vestluse käigus täita.</p>
      </section>

      <section class="block">
        <h3>Ajajoon</h3>
        <ul class="timeline">
          <li
            v-for="entry in [...kase.notes].reverse()"
            :key="entry.id"
            :class="{ maimu: entry.authorKind === 'maimu' }"
          >
            <div class="who">
              <strong>{{ entry.authorKind === "maimu" ? "mAImu" : (entry.author ?? "keegi") }}</strong>
              <span>{{ when(entry.createdAt) }}</span>
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -- markdown is escaped in renderMarkdown -->
            <div class="prose" v-html="renderMarkdown(entry.body)" />
          </li>
        </ul>
        <div class="add">
          <textarea v-model="note" rows="3" placeholder="Lisa märkus ajajoonele…" />
          <button
            class="btn"
            type="button"
            :disabled="busy || !note.trim()"
            @click="run(() => api.addCaseNote(kase!.id, note)).then(() => (note = ''))"
          >
            Lisa märkus
          </button>
        </div>
      </section>

      <section class="block">
        <h3>Vestlused</h3>
        <p v-if="!kase.conversations.length" class="empty">
          Ühtegi vestlust veel. "Uus vestlus siin" alustab lõime, mis teab toimiku sisu.
        </p>
        <ul v-else class="rows">
          <li v-for="conversation in kase.conversations" :key="conversation.id">
            <button type="button" @click="openConversation(conversation.id)">
              <span>{{ conversation.title }}</span>
              <span class="meta"
                >{{ conversation.owner }} · {{ conversation.messages }} sõnumit ·
                {{ when(conversation.updatedAt) }}</span
              >
            </button>
          </li>
        </ul>
      </section>

      <section class="block">
        <h3>Failid</h3>
        <p class="hint">Vestlustesse lisatud manused jõuavad siia ise.</p>
        <ul v-if="kase.files.length" class="rows">
          <li v-for="file in kase.files" :key="file.id">
            <a :href="`/api/attachments/${file.id}`" class="filelink">{{ file.name }}</a>
            <span class="meta">{{ kb(file.bytes) }} · {{ file.addedBy }} · {{ when(file.addedAt) }}</span>
          </li>
        </ul>
        <input type="file" multiple :accept="ATTACHMENT_ACCEPT" :disabled="busy" @change="onFiles" />
      </section>

      <section class="block">
        <h3>Liikmed</h3>
        <ul class="members">
          <li v-for="name in kase.members" :key="name">
            {{ name }}
            <button
              v-if="kase.members.length > 1"
              class="linkish"
              type="button"
              :disabled="busy"
              @click="run(() => api.removeCaseMember(kase!.id, name))"
            >
              {{ name === currentUser?.username ? "lahku" : "eemalda" }}
            </button>
          </li>
        </ul>
        <div class="add inline">
          <input v-model.trim="member" type="text" spellcheck="false" placeholder="kasutajanimi" />
          <button
            class="btn-ghost"
            type="button"
            :disabled="busy || !member"
            @click="run(() => api.addCaseMember(kase!.id, member)).then(() => (member = ''))"
          >
            Lisa liige
          </button>
        </div>
      </section>
    </template>

    <div v-if="reading" class="reader" @click.self="reading = null">
      <article>
        <header>
          <h3>{{ reading.title }}</h3>
          <button class="btn-ghost" type="button" @click="reading = null">Sulge</button>
        </header>
        <p class="hint">Kolleegi vestlus. Lugemiseks, jätkata saab selle omanik.</p>
        <div v-for="message in reading.messages" :key="message.id" class="bubble" :class="message.role">
          <!-- eslint-disable-next-line vue/no-v-html -- markdown is escaped in renderMarkdown -->
          <div class="prose" v-html="renderMarkdown(message.content)" />
        </div>
      </article>
    </div>
  </section>
</template>

<style scoped>
.toimik {
  max-width: 900px;
  margin: 0 auto;
  padding: 1.2rem 1rem 3rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

h2 {
  margin: 0.4rem 0 0.2rem;
  font-size: 1.15rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

h3 {
  margin: 0;
  font-size: 0.92rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.block {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 0.85rem 1rem;
  background: var(--paper);
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
}

.hint,
.meta {
  color: var(--muted);
  font-size: 0.78rem;
  margin: 0;
}

code {
  background: var(--soft);
  padding: 0.05rem 0.35rem;
  border-radius: 4px;
  font-size: 0.78rem;
}

textarea,
input[type="text"],
select {
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 0.5rem 0.6rem;
  font: inherit;
  font-size: 0.87rem;
  background: #fff;
  color: var(--ink);
  width: 100%;
  resize: vertical;
}

select {
  width: auto;
}

.timeline {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  max-height: 460px;
  overflow-y: auto;
}

.timeline li {
  border-left: 3px solid var(--line);
  padding: 0.1rem 0 0.1rem 0.7rem;
}

.timeline li.maimu {
  border-left-color: var(--green);
}

.who {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
  font-size: 0.76rem;
  color: var(--muted);
}

.add {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  align-items: flex-start;
}

.add.inline {
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}

.rows,
.members {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.members {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.6rem;
  font-size: 0.85rem;
}

.rows button {
  width: 100%;
  text-align: left;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: transparent;
  padding: 0.45rem 0.6rem;
  font: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.rows button:hover {
  border-color: var(--green);
}

.filelink {
  color: var(--green);
  font-size: 0.85rem;
}

.linkish {
  border: 0;
  background: none;
  padding: 0 0 0 0.3rem;
  font: inherit;
  font-size: 0.74rem;
  color: var(--muted);
  cursor: pointer;
  text-decoration: underline;
}

.empty {
  color: var(--muted);
  font-size: 0.84rem;
  margin: 0;
}

.error {
  margin: 0;
  font-size: 0.82rem;
  border: 1px solid var(--warn);
  border-radius: 8px;
  padding: 0.5rem 0.7rem;
  color: var(--warn);
}

.prose {
  font-size: 0.88rem;
  line-height: 1.5;
}

.prose :deep(p) {
  margin: 0.25rem 0;
}

.reader {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 10;
}

.reader article {
  background: var(--card);
  border-radius: 14px;
  padding: 1rem 1.2rem;
  max-width: 760px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.reader header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
}

.bubble {
  border-radius: 10px;
  padding: 0.5rem 0.7rem;
  background: var(--soft);
}

.bubble.user {
  background: transparent;
  border: 1px solid var(--line);
}
</style>
